<script setup>
/**
 * The files the agency sent this person by name (XN-4671): the Library's own
 * "Only for you" set, read the Library's own way, so the two can never
 * disagree. A file shared with a segment is not personal, however small the
 * segment. Prototype `cover()`, "Your documents".
 */
import { computed } from 'vue';
import { RouterLink } from 'vue-router';
import XlButton from '../xl/XlButton.vue';
import XlSkeleton from '../xl/XlSkeleton.vue';
import IconChevronRight from '../icons/IconChevronRight.vue';
import IconLock from '../icons/IconLock.vue';
import PolicyLineIcon from '../policyLines/PolicyLineIcon.vue';
import usePortalLibrary, { metaLine } from '../../composables/usePortalLibrary';

defineProps({ sender: { type: String, required: true } });

const SHOWN = 4;
const ONLY_FOR_YOU = { path: '/hub/library', query: { tab: 'mine' } };
const { filesOn, state, items, load } = usePortalLibrary();
const mine = computed(() => items.value.filter((item) => item.mine));
// The Files switch off means nothing is shared — the Library's answer too.
const phase = computed(() => (filesOn.value === false ? 'ready' : state.value));
</script>

<template>
  <section class="cov-docs" aria-labelledby="cov-docs-heading">
    <div class="cov-docs__sh">
      <h2 id="cov-docs-heading">Your documents</h2>
      <i class="cov-docs__ln" />
      <RouterLink v-if="phase === 'ready' && mine.length" :to="ONLY_FOR_YOU" class="cov-docs__lib">Your library</RouterLink>
    </div>

    <XlSkeleton v-if="phase === 'loading'" height="72px" />

    <div v-else-if="phase === 'error'" class="cov-docs__error" role="alert">
      <p>Your documents can't be shown right now.</p>
      <XlButton variant="ghost" size="sm" data-testid="documents-retry" @click="load">Retry</XlButton>
    </div>

    <template v-else-if="mine.length">
      <p class="cov-docs__note">Sent to you by name by {{ sender }} — these also sit in your library.</p>
      <div class="cov-docs__card">
        <RouterLink v-for="item in mine.slice(0, SHOWN)" :key="item.id" :to="`/hub/library/${item.id}`" class="cov-doc" data-testid="document-row">
          <span class="cov-doc__ico" aria-hidden="true"><IconLock color="currentColor" size="23" /></span>
          <span class="cov-doc__t"><b>{{ item.name }}</b><span>{{ metaLine(item) }}</span></span>
          <span class="cov-doc__tag">Only you</span>
          <IconChevronRight color="currentColor" width="9" height="16" class="cov-doc__chev" />
        </RouterLink>
        <RouterLink v-if="mine.length > SHOWN" :to="ONLY_FOR_YOU" class="cov-doc" data-testid="documents-more">
          <span class="cov-doc__ico" aria-hidden="true"><PolicyLineIcon name="book" :size="23" /></span>
          <span class="cov-doc__t">
            <b class="cov-doc__more">See all {{ mine.length }} in your library</b>
            <span>{{ mine.length - SHOWN }} more sent to you by name</span>
          </span>
          <IconChevronRight color="currentColor" width="9" height="16" class="cov-doc__chev" />
        </RouterLink>
      </div>
    </template>

    <div v-else class="cov-docs__none" data-testid="documents-none">
      <span class="cov-docs__none-ico" aria-hidden="true"><IconLock color="currentColor" size="22" /></span>
      <p>
        <b>Nothing here yet</b>
        Anything {{ sender }} sends you by name — a policy document, a renewal summary — appears here and in your library.
      </p>
    </div>
  </section>
</template>

<style scoped>
.cov-docs__sh { display: flex; align-items: baseline; gap: 12px; margin: 34px 0 14px; }
.cov-docs__sh h2 { margin: 0; font-size: 13px; font-weight: 600; letter-spacing: 0.1em; text-transform: uppercase; color: var(--xl-color-outline-variant); }
.cov-docs__ln { flex: 1; height: 1px; background: var(--xl-color-border); }
.cov-docs__lib { font-size: 13.5px; font-weight: 600; color: var(--xl-color-primary); text-decoration: none; }
.cov-docs__note { margin: -4px 0 12px; font-size: 13.5px; color: var(--xl-color-outline); }
.cov-docs__card, .cov-docs__none {
  background: #fff; border: 1px solid var(--xl-color-border); border-radius: var(--xl-radius-lg);
  padding: 8px 22px; box-shadow: var(--xl-shadow-1); margin-bottom: 14px;
}
.cov-doc { display: flex; align-items: center; gap: 14px; padding: 15px 0; border-bottom: 1px solid var(--xl-color-border-subtle); color: inherit; text-decoration: none; }
.cov-doc:last-child { border-bottom: 0; }
.cov-doc:focus-visible { outline: 2px solid var(--xl-color-primary); outline-offset: 2px; }
.cov-doc__ico { width: 42px; height: 42px; flex: 0 0 42px; border-radius: 13px; display: grid; place-items: center; background: var(--xl-color-primary-container); color: var(--xl-color-primary); }
.cov-doc__t { flex: 1; min-width: 0; }
.cov-doc__t b { display: block; font-size: 15px; overflow-wrap: anywhere; }
.cov-doc__t span { font-size: 13px; color: var(--xl-color-outline); }
.cov-doc__more { color: var(--xl-color-primary); }
.cov-doc__tag {
  display: inline-flex; align-items: center; height: 23px; padding: 0 9px; border-radius: 7px; flex: 0 0 auto;
  font-size: 11.5px; font-weight: 600; background: var(--xl-color-border-subtle); color: var(--xl-color-on-surface-variant); white-space: nowrap;
}
.cov-doc__chev { color: var(--xl-color-outline-variant); flex: 0 0 auto; }
.cov-docs__none { display: flex; gap: 13px; align-items: flex-start; padding: 22px 24px; }
.cov-docs__none-ico { width: 40px; height: 40px; flex: 0 0 40px; border-radius: 13px; display: grid; place-items: center; background: var(--xl-color-border-subtle); color: var(--xl-color-outline-variant); }
.cov-docs__none p { margin: 0; font-size: 14px; line-height: 1.55; color: var(--xl-color-on-surface-variant); }
.cov-docs__none b { display: block; font-size: 15px; color: var(--xl-color-on-surface); }
.cov-docs__error {
  display: flex; align-items: center; justify-content: space-between; gap: 14px; flex-wrap: wrap; padding: 18px 20px;
  border-radius: 16px; font-size: 14.5px; background: var(--xl-color-error-container, #FBEAEA); border: 1px solid var(--xl-color-error); color: var(--xl-color-error);
}
.cov-docs__error p { margin: 0; }
@media (max-width: 480px) { .cov-docs__card { padding: 4px 14px; } .cov-docs__none { padding: 18px; } }
</style>
